import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { StudioActionError } from '../StudioActionError';
import { useStudioActionFeedback } from '../useStudioActionFeedback';
import { useRejectReelMutation } from '../../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../../redux/api/utils/isSuccessfulResult';
import { SerializedReel } from '../../types';
import { joinReelRejectReason, REEL_REASON_MAX } from './reelReview';
import {
  reelConflictCode,
  ReelConflictCode,
  reelRefusalCode,
  ReelRefusalCode,
  reelRefusalResetsAt,
} from './reelRefusal';
import { ReelRejectChip, reelRejectChipMessages, reelRejectChips, reelRejectMessages } from './reelReviewMessages';
import { ReelReasonChips } from './ReelReasonChips';
import { ReelRejectNotesField } from './ReelRejectNotesField';
import { ReelRejectQuota } from './ReelRejectQuota';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the preview, the history and the sheets */
import styles from './ReelReview.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface ReelRejectSheetProps {
  isOpen: boolean;
  accountId: number;
  reel: SerializedReel;
  releaseTitle?: string;
  onClose: () => void;
  /** The parent is rejected and the new reel started: the screen opens the child. */
  onChild: (childUuid: string) => void;
  /** The parent is rejected, but no new reel started (402, 403, 503; design e3). */
  onRefused: (code: ReelRefusalCode, resetsAt: string | null) => void;
  /** A 409: the reel is no longer ready (design d4). */
  onConflict: (code: ReelConflictCode) => void;
}

/** The body of the open sheet. It mounts on open, so every open starts empty. */
const RejectForm: React.FC<Omit<ReelRejectSheetProps, 'isOpen'>> = (props) => {
  const { accountId, reel, releaseTitle, onClose, onChild, onRefused, onConflict } = props;
  const intl = useIntl();
  const [rejectReel, { isLoading, error }] = useRejectReelMutation();
  const [picked, setPicked] = useState<ReelRejectChip[]>([]);
  const [notes, setNotes] = useState('');

  const toggle = useCallback(
    (chip: ReelRejectChip) => setPicked((now) => (now.includes(chip) ? now.filter((c) => c !== chip) : [...now, chip])),
    []
  );
  // Design order, not click order, so the same chips always read the same.
  const reason = useMemo(
    () =>
      joinReelRejectReason(
        reelRejectChips
          .filter((chip) => picked.includes(chip))
          .map((chip) => intl.formatMessage(reelRejectChipMessages[chip])),
        notes
      ),
    [intl, notes, picked]
  );
  const length = reason?.length ?? 0;
  const isTooLong = length > REEL_REASON_MAX;

  const submit = useCallback(async () => {
    const result = await rejectReel({ accountId, reelUuid: reel.uuid, reason });
    if (isSuccessfulResult(result)) {
      onChild(result.data.child.uuid);
      return;
    }
    const refusal = reelRefusalCode(result.error);
    if (refusal !== null) {
      onRefused(refusal, reelRefusalResetsAt(result.error));
      return;
    }
    const conflict = reelConflictCode(result.error);
    if (conflict !== null) {
      onConflict(conflict);
    }
  }, [accountId, onChild, onConflict, onRefused, reason, reel.uuid, rejectReel]);

  const isOtherError = error !== undefined && reelRefusalCode(error) === null && reelConflictCode(error) === null;
  const rejectFeedback = useStudioActionFeedback('reject the AI reel', isOtherError ? error : undefined);

  return (
    <>
      <ModalHeader toggle={onClose}>
        {intl.formatMessage(reelRejectMessages.title)}
        <small className="d-block text-muted fw-normal mt-1">{intl.formatMessage(reelRejectMessages.intro)}</small>
      </ModalHeader>
      <ModalBody className="d-flex flex-column gap-3">
        <StudioActionError feedback={rejectFeedback} />
        <div className={styles.field}>
          <span className={styles.label}>{intl.formatMessage(reelRejectMessages.whatIsWrong)}</span>
          <ReelReasonChips picked={picked} onToggle={toggle} />
        </div>
        <ReelRejectNotesField notes={notes} onChange={setNotes} reasonLength={length} />
        <ReelRejectQuota releaseTitle={releaseTitle} />
      </ModalBody>
      <ModalFooter>
        <SMLButton outline omitCap onClick={onClose}>
          {intl.formatMessage(reelRejectMessages.cancel)}
        </SMLButton>
        <SMLButton
          color="primary"
          omitCap
          leftIcon={faSparkles}
          disabled={isTooLong || isLoading}
          isLoading={isLoading}
          onClick={submit}
        >
          {intl.formatMessage(reelRejectMessages.submit)}
        </SMLButton>
      </ModalFooter>
    </>
  );
};

/**
 * The reject sheet (design e1). The picked chips and the notes go as one reason, 500 characters
 * at most. Every reject makes a child reel and takes one video credit.
 */
const ReelRejectSheetComponent: React.FC<ReelRejectSheetProps> = (props) => {
  const { isOpen, accountId, reel, releaseTitle, onClose, onChild, onRefused, onConflict } = props;
  return (
    <Modal isOpen={isOpen} toggle={onClose} centered className="sml-modal">
      <RejectForm
        accountId={accountId}
        reel={reel}
        releaseTitle={releaseTitle}
        onClose={onClose}
        onChild={onChild}
        onRefused={onRefused}
        onConflict={onConflict}
      />
    </Modal>
  );
};

export const ReelRejectSheet = TypedMemo(ReelRejectSheetComponent);
